import { DialogContainer, Flex } from '@adobe/react-spectrum';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { commonApi, excApi, handleApiCall } from '@exchange-frontends/api';
import { EcAcquiredDialog, WithSkeleton } from '@exchange-frontends/components';
import {
  useHandleApiCall,
  useIMS,
  useIsMounted,
  useMediaQuery,
  useRootHref,
  useSelectedOrg,
} from '@exchange-frontends/custom-hooks';

// If we do not import this here, the ec details pages will break because the image is not resolved.
// This is due to code splitting added in the main App.js
import '../../../../../../static/illustrations/Illu_ConsentFlow_320x180.png';

import useConsent from '../../../../../actions/entitlementApi/selectors/ecAppDetails/useConsent';
import { ContainerContext } from '../../../../../utils';
import { useStore } from '../../../store';
import { ConfigContext, UserDataContext } from '../../App';
import { AppDetailsContext } from '../AppDetails';
import CcPaidAppButton from './CCPaidAppButton';
import CcFreeAppButton from './CcFreeAppButton';
import CcTrialAppButton from './CcTrialAppButton';
import EcFreeAppButton from './EcFreeAppButton';
import EcPaidAppButton from './EcPaidAppButton';
import InstallationToast from './InstallationToast';
import ManageButton from './ManageButton';
import PluginAcquiredDialog from './PluginAcquiredDialog';

export const HeaderButtonsContext = React.createContext({
  selectedOrg: null,
  fetchCreativeCloudLink: async () => {},
  setShowPluginAcquiredDialog: () => {},
});

const HeaderButtons = () => {
  const { cloud } = useContext(AppDetailsContext);
  const { id } = useParams();
  const ims = useIMS();
  const selectedOrg = useSelectedOrg();
  const isMobileSized = useMediaQuery('(max-width: 500px)');
  const acquired = useStore((state) => state.acquired);
  const trialAppAcquired = useStore((state) => state.trialAppAcquired);
  const [showPluginAcquiredDialog, setShowPluginAcquiredDialog] = useState(false);
  const { profile: userData, accessToken } = useContext(UserDataContext) ?? {};
  const { ROUTES } = useContext(ConfigContext) ?? {};
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const manageLink = useRootHref(ROUTES.MANAGE ?? '');

  const detailScreen = useStore((state) => state.detailScreen);
  const { pricingInfo, trialInfo, appType } = detailScreen ?? {};
  const { isTrial, linkedAppId } = trialInfo || {};
  const hasLinkedTrial = Boolean(linkedAppId && !isTrial);
  const shouldShowBuyButton = !acquired && !isTrial && pricingInfo?.price > 0;
  const shouldShowFreeButton = !acquired && !isTrial && pricingInfo?.price === 0;
  const shouldShowStartTrialButton = isTrial ? !acquired : !acquired && !trialAppAcquired && linkedAppId;

  const consentQuery = useConsent(
    { enabled: acquired && appType === 'S2S', refetchOnMount: true },
    { appId: id, orgId: selectedOrg?.code }
  );

  const fetchCreativeCloudLink = useCallback(
    async (actionType) => {
      const route = 'manage';
      const action = actionType ?? 'view';
      const params = { pluginId: id };
      try {
        const handler = handleApiCall(commonApi.getCreativeCloudLink, { route, action, params });
        const response = await handler.run();
        if (isMounted.current) {
          window.location.href = `${response?.deeplinkUrl}&guid=${userData?.userId}`;
        }
      } catch (err) {
        console.error(err);
      }
    },
    [id, isMounted, userData]
  );

  const closePluginAcquiredDialog = () => {
    setShowPluginAcquiredDialog(false);
  };

  /* GET CC APP ENTITLEMENT STATUS */
  useHandleApiCall({
    shouldFetch: Boolean(accessToken && id && !isTrial && cloud === 'cc'),
    handler: useMemo(
      () => handleApiCall(excApi.checkEntitlements, { appId: id, accessToken: accessToken }),
      [accessToken, id]
    ),
    onSuccess: useCallback(
      (response) => {
        useStore.setState({ acquired: response, hasEntitlementInfo: true });
        if (sessionStorage.getItem('redirected-to-fs') === id && response) {
          sessionStorage.removeItem('redirected-to-fs');
          fetchCreativeCloudLink('install');
          setShowPluginAcquiredDialog(true);
        }
      },
      [fetchCreativeCloudLink, id]
    ),
  });

  /* GET EC APP ENTITLEMENT STATUS */
  useHandleApiCall({
    shouldFetch: Boolean(accessToken && id && selectedOrg?.code && cloud === 'ec'),
    handler: useMemo(
      () => handleApiCall(excApi.checkEnterpriseEntitlements, { orgId: selectedOrg?.code, appId: id, accessToken }),
      [accessToken, id, selectedOrg?.code]
    ),
    onSuccess: useCallback((response) => useStore.setState({ acquired: response, hasEntitlementInfo: true }), []),
  });

  /* GET ENTITLEMENT STATUS FOR TRIAL PAGE */
  useHandleApiCall({
    shouldFetch: Boolean(accessToken && linkedAppId && id && isTrial),
    handler: useMemo(
      () => handleApiCall(excApi.checkAcquiredApps, { appId: id, accessToken, linkedAppId, isTrial }),
      [accessToken, linkedAppId, id, isTrial]
    ),
    onSuccess: useCallback((response) => useStore.setState({ acquired: response, hasEntitlementInfo: true }), []),
  });

  /* GET LINKED TRIAL ENTITLEMENT STATUS ON PARENT PAGE */
  useHandleApiCall({
    shouldFetch: Boolean(accessToken && id && hasLinkedTrial),
    handler: useMemo(
      () =>
        handleApiCall(excApi.checkAcquiredApps, {
          appId: linkedAppId,
          accessToken,
          linkedAppId: id,
          isTrial,
        }),
      [accessToken, linkedAppId, id, isTrial]
    ),
    onSuccess: useCallback(
      (response) => useStore.setState({ trialAppAcquired: response, hasTrialEntitlementInfo: true }),
      []
    ),
  });

  const { orgsLoaded, orgs } = useContext(ContainerContext);
  const hasNoOrg = orgsLoaded && !orgs?.length;
  const hasEntitlementInfo = useStore((state) => state.hasEntitlementInfo);
  const hasTrialEntitlementInfo = useStore((state) => state.hasTrialEntitlementInfo);
  const entitlementsLoaded = (hasEntitlementInfo || hasNoOrg) && (!hasLinkedTrial || hasTrialEntitlementInfo);
  const isNotSignedIn = ims?.initialized && !ims?.isSignedInUser();
  const isAdmin = selectedOrg?.role === 'ADMIN';

  return (
    <HeaderButtonsContext.Provider
      value={{
        selectedOrg,
        fetchCreativeCloudLink,
        setShowPluginAcquiredDialog,
      }}
    >
      <Flex
        alignItems="center"
        justifyContent="end"
        marginEnd={isMobileSized ? '8px' : '30px'}
        marginStart={isMobileSized ? 'auto' : '0px'}
      >
        <WithSkeleton
          shouldRender={entitlementsLoaded || isNotSignedIn}
          borderRadius="4px"
          width={isMobileSized ? 50 : 200}
          height={30}
        >
          <Flex
            direction={isMobileSized ? 'column' : 'row'}
            alignItems={isMobileSized ? 'end' : 'center'}
            justifyContent="space-between"
            gap={isMobileSized ? '8px' : '20px'}
          >
            {shouldShowBuyButton && (
              <>
                {cloud === 'ec' && <EcPaidAppButton />}
                {cloud === 'cc' && <CcPaidAppButton />}
              </>
            )}
            {shouldShowFreeButton && (
              <>
                {cloud === 'ec' && <EcFreeAppButton />}
                {cloud === 'cc' && <CcFreeAppButton />}
              </>
            )}
            {acquired && <ManageButton isTrial={isTrial} />}
            {shouldShowStartTrialButton && <CcTrialAppButton />}
            {trialAppAcquired && <ManageButton isTrial={true} />}
          </Flex>
        </WithSkeleton>
      </Flex>
      {showPluginAcquiredDialog && (
        <>
          {cloud === 'ec' && (
            <DialogContainer>
              {appType === 'ABD' && (
                <PluginAcquiredDialog
                  userData={userData}
                  onAccept={() => {
                    closePluginAcquiredDialog();
                    navigate(`${manageLink}/apps/ec/${selectedOrg.code}/ABD/view/${id}`);
                  }}
                  close={closePluginAcquiredDialog}
                />
              )}
              {appType === 'S2S' && !consentQuery.isLoading && (
                <EcAcquiredDialog
                  isAdmin={isAdmin}
                  close={closePluginAcquiredDialog}
                  dialogVariant="applicationAcquired"
                  appId={id}
                  orgId={selectedOrg?.code}
                  appType={detailScreen?.appType}
                  scopes={detailScreen?.scopeDescription}
                  clientId={consentQuery?.data?.consent?.clientId}
                />
              )}
            </DialogContainer>
          )}
          {cloud === 'cc' && (
            <InstallationToast
              close={closePluginAcquiredDialog}
              onInstallClick={() => fetchCreativeCloudLink('install')}
            />
          )}
        </>
      )}
    </HeaderButtonsContext.Provider>
  );
};

export default HeaderButtons;
